import { createContext, type ReactNode, useCallback, useMemo, useRef } from 'react';
import { useCompatRouter } from '@/src/lib/router-compat/useCompatRouter';
import type { FilterState } from './filterTypes';
import { DEFAULT_FILTERS, FILTER_URL_KEYS } from './filterTypes';
import { deserializeFromUrl, serializeToUrl } from './filterSerializer';
import { applyFilterRemoval } from './applyFilterRemoval';
import { type FilterPill, getActiveFilterCount } from './filterLabels';
import { useFilterLabels } from './useFilterLabels';

export type FilterContextValue = {
  filters: FilterState;
  /**
   * A whole FilterState, or an updater resolved against the latest filters. Use the updater from
   * anything that writes after an `await` (HP-9100). Two caveats: updaters do not compose the way
   * React's setState does - each call resolves against the last rendered filters and overwrites
   * both the pending state and the url, so two calls in one batch keep only the second. And the
   * prop types below this context (SearchFiltersProvider, AllFiltersModal, useListingSearchState)
   * still take a whole FilterState, so callers under them cannot pass an updater yet.
   */
  setFilters: (next: FilterState | ((prev: FilterState) => FilterState)) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  activeFilterPills: Array<FilterPill>;
  removeFilter: (key: keyof FilterState, value?: string) => void;
  /**
   * SRP only: false until the user changes a filter, then true until the next
   * slug navigation. Lets the live search mirror SSR before interaction and
   * base-anchor afterwards. Undefined for the legacy (homepage) provider.
   */
  hasInteracted?: boolean;
  /**
   * SRP only: increments on every setFilters/resetFilters commit. Unlike
   * hasInteracted it never resets on navigation, so consumers can react to
   * each interaction even when the applied values are unchanged.
   */
  interactionSeq?: number;
};

export const FilterContext = createContext<FilterContextValue | null>(null);

/**
 * Extracts only filter-relevant query params as a stable string key.
 * Much cheaper than JSON.stringify(fullQuery) since it ignores resourceOrMaloneId,
 * searchSlug, and any other non-filter params that change on navigation.
 */
function extractFilterKey(query: Record<string, string | Array<string> | undefined>): string {
  const parts: Array<string> = [];
  for (const key of FILTER_URL_KEYS) {
    const val = query[key];
    if (val !== undefined) {
      parts.push(`${key}=${Array.isArray(val) ? val[0] : val}`);
    }
  }
  return parts.join('&');
}

/**
 * On the client, router.query updates asynchronously during navigation.
 * When navigating from homepage → SRP, the URL has already changed but
 * router.query may still reflect the old page (missing resourceOrMaloneId and
 * filter params). Reading window.location.search gives us the real
 * query string synchronously so filters carry over immediately.
 */
function getQueryWithLocationFallback(
  routerQuery: Record<string, string | Array<string> | undefined>,
): Record<string, string | Array<string> | undefined> {
  const hasRouteParams = typeof routerQuery.resourceOrMaloneId === 'string';
  if (hasRouteParams || typeof window === 'undefined') return routerQuery;

  // Check if the actual URL has filter params that router.query is missing
  const search = window.location.search;
  if (!search) return routerQuery;

  const urlParams = new URLSearchParams(search);
  const hasFilterInUrl = FILTER_URL_KEYS.some((key) => urlParams.has(key));
  if (!hasFilterInUrl) return routerQuery;

  // The URL has already navigated but router.query is stale — build
  // a merged query from the pathname segments + search params.
  const merged: Record<string, string> = {};
  // Extract dynamic route params from pathname (e.g., /berkeley-ca/apartments-for-rent)
  const segments = window.location.pathname.split('/').filter(Boolean);
  if (segments.length >= 2) {
    merged.resourceOrMaloneId = segments[0];
    merged.searchSlug = segments[1];
  }
  for (const [key, value] of urlParams.entries()) {
    merged[key] = value;
  }
  return merged;
}

/**
 * @deprecated Never mounted - the app is App Router only, so FilterProviderAppRouter is the live
 * provider. Only `FilterContext` and `FilterContextValue` from this module are used. Its updater
 * branch below is the minimum needed to compile against the shared widened type, not a second
 * supported implementation: it resolves against the latest filters but still rebuilds the query
 * from a `[router]`-captured `router.query`, so it does not carry the HP-9100 guarantee. Delete
 * this component rather than extend it.
 */
export function FilterProvider({ children }: { children: ReactNode }) {
  const router = useCompatRouter();
  const query = getQueryWithLocationFallback(router.query);

  // Check route params rather than router.isReady to avoid SSR/client mismatch.
  const hasRouteParams = typeof query.resourceOrMaloneId === 'string';

  const filterKey = extractFilterKey(query);

  const filters = useMemo(
    () => (hasRouteParams ? deserializeFromUrl(query) : DEFAULT_FILTERS),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKey is a stable derivative of query's filter params
    [hasRouteParams, filterKey],
  );

  const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);

  const { getActiveFilterPills } = useFilterLabels();
  const activeFilterPills = useMemo(
    () => getActiveFilterPills(filters),
    [filters, getActiveFilterPills],
  );

  const filterKeyRef = useRef(filterKey);
  filterKeyRef.current = filterKey;

  // Ref, not a dep: an updater resolves against the latest filters without re-creating setFilters
  // and cascading re-renders to every consumer.
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const setFilters = useCallback<FilterContextValue['setFilters']>(
    (update) => {
      const next = typeof update === 'function' ? update(filtersRef.current) : update;
      const currentQuery = router.query;
      const nonFilterParams: Record<string, string | Array<string> | undefined> = {};
      for (const [k, v] of Object.entries(currentQuery)) {
        if (!(FILTER_URL_KEYS as ReadonlyArray<string>).includes(k)) {
          nonFilterParams[k] = v;
        }
      }

      const filterParams = serializeToUrl(next);
      const newQuery: Record<string, string | Array<string> | undefined> = {
        ...nonFilterParams,
        ...filterParams,
      };

      // Reset pagination on filter change
      delete newQuery.page;

      // No-op guard: compare the proposed filter key against the current one
      // instead of doing a full deserialize+serialize round-trip
      const proposedKey = extractFilterKey(newQuery);
      if (proposedKey === filterKeyRef.current) return;

      router.push({ query: newQuery }, undefined, { shallow: true, scroll: false });
    },
    [router],
  );

  const resetFilters = useCallback(() => {
    const currentQuery = router.query;
    const nonFilterParams: Record<string, string | Array<string> | undefined> = {};
    for (const [k, v] of Object.entries(currentQuery)) {
      if (!(FILTER_URL_KEYS as ReadonlyArray<string>).includes(k)) {
        nonFilterParams[k] = v;
      }
    }

    delete nonFilterParams.page;

    router.push({ query: nonFilterParams }, undefined, { shallow: true, scroll: false });
  }, [router]);

  const removeFilter = useCallback(
    (key: keyof FilterState, value?: string) => {
      setFilters((prev) => applyFilterRemoval(prev, key, value));
    },
    [setFilters],
  );

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      resetFilters,
      activeFilterCount,
      activeFilterPills,
      removeFilter,
    }),
    [filters, setFilters, resetFilters, activeFilterCount, activeFilterPills, removeFilter],
  );

  return <FilterContext.Provider value={contextValue}>{children}</FilterContext.Provider>;
}
